import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';

import TemplatesWhatsappPage from '../templates-whatsapp';
import TemplatesMessengerPage from '../templates-messenger';

/**
 * Casca de abas da tela de Templates — separa a visualização por plataforma.
 *
 * Deliberadamente sem lógica de template: cada aba renderiza o componente da sua
 * plataforma, que mantém o próprio seletor de canal e o próprio fetch. Nada do
 * código de whatsapp_business é alterado.
 *
 * A aba de uma plataforma só existe se o cliente tiver canal daquela plataforma; com
 * uma só, a barra de abas nem aparece — quem só tem WhatsApp vê a tela exatamente
 * como antes.
 */

const PLATAFORMAS = [
    {
        value: 'whatsapp_business',
        label: 'WhatsApp Business',
        icon: 'hugeicons:whatsapp-business',
        Componente: TemplatesWhatsappPage
    },
    {
        value: 'messenger',
        label: 'Messenger',
        icon: 'logos:messenger',
        Componente: TemplatesMessengerPage
    }
];

export default function TemplatesPage() {
    const { user, cliente } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [canais, setCanais] = useState(null); // null = ainda carregando

    const clienteId = cliente?._id || user?.clienteId;

    useEffect(() => {
        if (!clienteId) return;

        actionListaCanaisPorCliente(clienteId)
            .then((res) => setCanais(res.data?.data || []))
            .catch(() => setCanais([]));
    }, [clienteId]);

    const abas = useMemo(
        () => (canais ? PLATAFORMAS.filter((p) => canais.some((c) => c.platform === p.value)) : []),
        [canais]
    );

    const abaDaUrl = searchParams.get('platform');
    const abaAtiva = abas.some((a) => a.value === abaDaUrl) ? abaDaUrl : abas[0]?.value;

    const trocarAba = (value) => {
        const params = new URLSearchParams(searchParams);
        params.set('platform', value);
        setSearchParams(params, { replace: true });
    };

    // Enquanto não sabemos as plataformas, não renderiza aba nenhuma — pintar a aba
    // errada e trocar depois faria a tela piscar e disparar fetch descartado.
    if (canais === null) {
        return (
            <div className="w-full pt-2">
                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]"
                        style={{ width: '40%' }}
                    />
                </div>
            </div>
        );
    }

    // Nenhum canal com suporte a template: mostra a tela do WhatsApp, que já tem o
    // próprio estado vazio explicando o que fazer.
    if (abas.length === 0) return <TemplatesWhatsappPage />;

    // Uma só plataforma: sem barra de abas, a tela fica idêntica à de antes
    if (abas.length === 1) {
        const { Componente } = abas[0];
        return <Componente />;
    }

    return (
        <Tabs value={abaAtiva} onValueChange={trocarAba} className="w-full flex flex-col min-h-full bg-[#f4f4f7]" style={{ fontFamily: "'Plus Jakarta Sans', Manrope, ui-sans-serif, system-ui, sans-serif" }}>
            <div className="sticky top-0 z-30 bg-white border-b border-[#e7e7ec] px-4 md:px-6 lg:px-8">
                <TabsList className="flex h-auto w-full items-center justify-start gap-[26px] rounded-none bg-transparent p-0 mt-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {abas.map((aba) => (
                        <TabsTrigger
                            key={aba.value}
                            value={aba.value}
                            className="flex-shrink-0 flex items-center gap-2 rounded-none bg-transparent px-0.5 py-0 pt-2.5 pb-3 text-[14px] font-medium text-[#71717a] whitespace-nowrap transition-colors hover:text-[#18181b] data-[state=active]:bg-transparent data-[state=active]:text-[#6d28d9] data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_#6d28d9]"
                        >
                            <Iconify icon={aba.icon} width={16} />
                            {aba.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </div>

            {abas.map(({ value, Componente }) => (
                <TabsContent key={value} value={value} className="mt-0">
                    {abaAtiva === value && <Componente />}
                </TabsContent>
            ))}
        </Tabs>
    );
}
